<div class="flex flex-col gap-6 animate-in fade-in duration-500">
    <!-- Search and Date Filters -->
    <div class="flex flex-col xl:flex-row xl:items-center gap-4 p-4 bg-white rounded-2xl shadow-sm border border-slate-100">
        <!-- Left cluster: search + how the rows are grouped -->
        <div class="flex flex-wrap items-center gap-3 flex-1 min-w-0">
            <div class="w-full sm:w-56 lg:w-64">
                <p-iconField iconPosition="left" class="w-full">
                    <p-inputIcon styleClass="pi pi-search text-slate-400" />
                    <input
                        type="text"
                        pInputText
                        [(ngModel)]="searchText"
                        [placeholder]="'SEARCH' | translate"
                        class="w-full !h-[44px] !rounded-xl !pl-10 !bg-slate-50 !border-none focus:!ring-2 focus:!ring-primary/10 transition-all"
                        (keydown.enter)="applyFilters()">
                </p-iconField>
            </div>

            <!-- Group by: room (vs clinic hours) or doctor (vs that doctor's shift hours) -->
            <div class="inline-flex items-center h-[44px] p-1 bg-slate-100 rounded-xl shrink-0">
                <button type="button"
                    (click)="setGroupBy('room')"
                    [ngClass]="groupBy === 'room' ? 'bg-white text-slate-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'"
                    class="px-3 h-full rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all">
                    {{ 'GROUP_BY_ROOM' | translate }}
                </button>
                <button type="button"
                    (click)="setGroupBy('doctor')"
                    [ngClass]="groupBy === 'doctor' ? 'bg-white text-slate-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'"
                    class="px-3 h-full rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all">
                    {{ 'GROUP_BY_DOCTOR' | translate }}
                </button>
            </div>
        </div>

        <!-- Right cluster: date range + actions -->
        <div class="flex flex-wrap items-center gap-2 xl:justify-end shrink-0">
            <!-- Date Inputs -->
            <div class="flex items-center gap-2">
                <div class="w-32 lg:w-36">
                    <p-datepicker
                        [(ngModel)]="startDate"
                        [placeholder]="'START_DATE' | translate"
                        [showIcon]="true"
                        [maxDate]="maxDate"
                        iconDisplay="input"
                        appendTo="body"
                        inputStyleClass="w-full !h-[44px] !border-slate-200 !rounded-xl !text-sm focus:!border-primary focus:!shadow-none"
                        styleClass="w-full">
                    </p-datepicker>
                </div>
                <div class="w-32 lg:w-36">
                    <p-datepicker
                        [(ngModel)]="endDate"
                        [placeholder]="'END_DATE' | translate"
                        [showIcon]="true"
                        [maxDate]="maxDate"
                        iconDisplay="input"
                        appendTo="body"
                        inputStyleClass="w-full !h-[44px] !border-slate-200 !rounded-xl !text-sm focus:!border-primary focus:!shadow-none"
                        styleClass="w-full">
                    </p-datepicker>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center gap-2">
                <app-button
                    [label]="'today_filter' | translate"
                    icon="pi pi-calendar"
                    (onClick)="filterToday()"
                    variant="secondary"
                    class="shrink-0">
                </app-button>

                <app-button
                    [label]="'apply_filter' | translate"
                    icon="pi pi-filter"
                    (onClick)="applyFilters()"
                    class="shrink-0">
                </app-button>

                <app-button
                    icon="pi pi-print"
                    (onClick)="printReport()"
                    variant="secondary"
                    [disabled]="loading || occupancyData.length === 0"
                    class="shrink-0">
                </app-button>

                <p-button
                    *ngIf="hasCustomFilters"
                    icon="pi pi-filter-slash"
                    [rounded]="true"
                    severity="secondary"
                    variant="text"
                    (onClick)="clearFilters()"
                    [pTooltip]="'clear_filters' | translate"
                    tooltipPosition="bottom"
                    styleClass="!h-[44px] !w-[44px] !text-slate-400 hover:!bg-slate-50">
                </p-button>
            </div>
        </div>
    </div>

    <!-- Clinic opening hours drive the "available hours" denominator. Say so out
         loud when they aren't configured rather than passing off a guess as fact. -->
    <div *ngIf="usesFallbackHours"
        class="flex items-start gap-2 px-4 py-3 -mb-2 rounded-xl bg-amber-50 border border-amber-100 text-amber-700">
        <i class="pi pi-info-circle mt-0.5"></i>
        <span class="text-xs font-medium">{{ 'CLINIC_HOURS_NOT_SET' | translate }}</span>
    </div>

    <!-- Table Section -->
    <div class="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden min-h-[500px] flex flex-col">
        <app-prime-table
            [columns]="cols"
            [data]="occupancyData"
            [loading]="loading"
            [paginator]="occupancyData.length < pageSize && currentPage === 1 ? false : true"
            [rows]="pageSize"
            [totalRecords]="totalRecords"
            [lazy]="true"
            (onLazyLoad)="onLazyLoad($event)"
            [templates]="{
                occupancy_percentage: occupancyTemplate,
                scheduled_occupancy_percentage: scheduledOccupancyTemplate,
                available_hours: availableHoursTemplate,
                scheduled_hours: scheduledHoursTemplate,
                booked_hours: bookedHoursTemplate,
                idle_hours: idleHoursTemplate
            }"
            class="flex-1">
        </app-prime-table>
    </div>

</div>

<ng-template #occupancyTemplate let-row>
    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all"
          [ngClass]="percentageClass(row.occupancy_percentage)">
        {{ formatPercentage(row.occupancy_percentage) }}
    </span>
</ng-template>

<ng-template #scheduledOccupancyTemplate let-row>
    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all"
          [ngClass]="percentageClass(row.scheduled_occupancy_percentage)">
        {{ formatPercentage(row.scheduled_occupancy_percentage) }}
    </span>
</ng-template>

<ng-template #availableHoursTemplate let-row>
    <span class="font-medium tabular-nums">{{ formatHours(row.available_hours) }}</span>
</ng-template>

<ng-template #scheduledHoursTemplate let-row>
    <span class="font-medium tabular-nums">{{ formatHours(row.scheduled_hours) }}</span>
</ng-template>

<ng-template #bookedHoursTemplate let-row>
    <span class="font-semibold tabular-nums text-slate-800">{{ formatHours(row.booked_hours) }}</span>
</ng-template>

<ng-template #idleHoursTemplate let-row>
    <span class="font-medium tabular-nums text-slate-500">{{ formatHours(row.idle_hours) }}</span>
</ng-template>